import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { map, switchMap, catchError, mergeMap, take, tap } from 'rxjs/operators';
import { selectDrawingsCurrentFloorId } from './drawings.selectors';
import { DRAWINGS_SERVICE } from '../../services/drawings/drawings.service.base';
import { IDrawingsService } from '../../interfaces/services/drawings-service.interface';
import { ToastService } from '../../services/toast.service';
import { getErrorMessage } from '../../utils/error.utils';
import { FILE_UPLOAD_FAILED_MESSAGE, isUploadFailedStatus } from '../../utils/upload-retry.utils';
import * as DrawingsActions from './drawings.actions';

@Injectable()
export class DrawingsEffects {
  private actions$ = inject(Actions);
  private drawingsService = inject<IDrawingsService>(DRAWINGS_SERVICE);
  private toastService = inject(ToastService);
  private store = inject(Store);
  private currentFloorId$ = this.store.select(selectDrawingsCurrentFloorId).pipe(take(1));

  loadDrawings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DrawingsActions.loadDrawings),
      switchMap(({ floorId }) =>
        this.drawingsService.getDrawings(floorId).pipe(
          map((drawings) => DrawingsActions.loadDrawingsSuccess({ drawings })),
          catchError((error) => of(DrawingsActions.loadDrawingsFailure({ error: getErrorMessage(error) })))
        )
      )
    )
  );

  addDrawing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DrawingsActions.addDrawing),
      mergeMap(({ floorId, name, fileSize, file }) =>
        this.drawingsService.addDrawing(floorId, name, fileSize, file).pipe(
          switchMap((drawing) =>
            this.drawingsService.getDrawings(floorId).pipe(
              mergeMap((drawings) =>
                this.currentFloorId$.pipe(map((currentFloorId) => ({ drawings, currentFloorId })))
              ),
              mergeMap(({ drawings, currentFloorId }) => {
                const persisted = drawings.find((d) => d.id === drawing.id);
                // The upload runs in the background; only refresh the list if the user is still on this floor.
                const refresh =
                  currentFloorId === floorId ? [DrawingsActions.loadDrawingsSuccess({ drawings })] : [];
                if (persisted && isUploadFailedStatus(persisted.status)) {
                  return from([
                    ...refresh,
                    DrawingsActions.addDrawingFailure({
                      error: FILE_UPLOAD_FAILED_MESSAGE,
                    }),
                  ]);
                }
                return from([
                  ...refresh,
                  DrawingsActions.addDrawingSuccess({ drawing: persisted ?? drawing }),
                ]);
              })
            )
          ),
          catchError((error) =>
            this.currentFloorId$.pipe(
              mergeMap((currentFloorId) =>
                from([
                  DrawingsActions.addDrawingFailure({
                    error: getErrorMessage(error),
                  }),
                  ...(currentFloorId === floorId ? [DrawingsActions.loadDrawings({ floorId })] : []),
                ])
              )
            )
          )
        )
      )
    )
  );

  addDrawingSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DrawingsActions.addDrawingSuccess),
        tap(() => {
          this.toastService.success('Drawing added successfully.', 'Success');
        })
      ),
    { dispatch: false }
  );

  addDrawingFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DrawingsActions.addDrawingFailure),
        tap(({ error }) => {
          this.toastService.error(error, 'Error');
        })
      ),
    { dispatch: false }
  );

  retryProcessingDrawing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DrawingsActions.retryProcessingDrawing),
      mergeMap(({ floorId, drawingId, fileSize }) =>
        this.drawingsService.retryProcessingDrawing(drawingId, fileSize).pipe(
          mergeMap(() =>
            from([
              DrawingsActions.retryProcessingDrawingSuccess(),
              DrawingsActions.loadDrawings({ floorId }),
            ])
          ),
          catchError((error) =>
            from([
              DrawingsActions.retryProcessingDrawingFailure({ error: getErrorMessage(error) }),
              DrawingsActions.loadDrawings({ floorId }),
            ])
          )
        )
      )
    )
  );

  retryProcessingDrawingSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DrawingsActions.retryProcessingDrawingSuccess),
        tap(() => {
          this.toastService.success('Drawing processing retried successfully.', 'Success');
        })
      ),
    { dispatch: false }
  );

  retryProcessingDrawingFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DrawingsActions.retryProcessingDrawingFailure),
        tap(({ error }) => {
          this.toastService.error(error, 'Error');
        })
      ),
    { dispatch: false }
  );
}
